/**
 * High-Intent SEO Keywords for California Residential Solar
 * Targeting homeowners escaping PG&E, SCE, and SDG&E utility rate hikes
 * 
 * Research-backed keywords optimized for:
 * - Google organic search rankings
 * - AI/LLM answer engines (Perplexity, ChatGPT, etc.)
 * - Featured snippets and Answer Engine Optimization (AEO)
 */

export interface SEOKeyword {
  id: string;
  keyword: string;
  searchVolume: "high" | "medium" | "low";
  intent: "transactional" | "informational" | "commercial" | "navigational";
  difficulty: "high" | "medium" | "low";
  category: "utility-escape" | "cost-savings" | "nem-3" | "battery-storage" | "local-seo" | "comparison" | "general";
  targetPage: string;
  metaTitle: string;
  metaDescription: string;
  h1Suggestion: string;
  relatedKeywords: string[];
  faqQuestions: string[];
  internalLinks: string[];
}

// ============================================
// TOP 30 HIGH-INTENT KEYWORDS
// ============================================

export const seoKeywords: SEOKeyword[] = [
  // ========== UTILITY ESCAPE KEYWORDS (High Intent) ==========
  {
    id: "escape-pge-rates",
    keyword: "escape PG&E rate hikes",
    searchVolume: "medium",
    intent: "transactional",
    difficulty: "low",
    category: "utility-escape",
    targetPage: "/blog/pge-rate-hike-2026-solar-escape",
    metaTitle: "Escape PG&E Rate Hikes 2026 | $ Zero Down Solar + Battery",
    metaDescription: "PG&E rates up 8-10% yearly. Lock in lower rates with $Zero down solar + battery. See your savings today.",
    h1Suggestion: "How to Escape PG&E Rate Hikes in 2026",
    relatedKeywords: ["PG&E rate increase 2026", "PG&E electricity rates", "alternative to PG&E"],
    faqQuestions: [
      "How much are PG&E rates increasing in 2026?",
      "Can solar help me avoid PG&E rate hikes?",
      "What's the best way to escape high PG&E bills?"
    ],
    internalLinks: ["/savings-estimate", "/blog", "/compare/utility-vs-ppa"]
  },
  {
    id: "escape-sce-rates",
    keyword: "escape SCE rate hikes",
    searchVolume: "medium",
    intent: "transactional",
    difficulty: "low",
    category: "utility-escape",
    targetPage: "/blog/sce-rate-hike-2026-solar-escape",
    metaTitle: "Escape SCE Rate Hikes 2026 | Solar + Battery $ Zero Down",
    metaDescription: "SCE rates rising 12.9% in 2026. Protect your budget with $Zero down solar + battery storage.",
    h1Suggestion: "Escape Southern California Edison Rate Hikes",
    relatedKeywords: ["SCE rate increase 2026", "SCE electricity rates", "Southern California Edison rates"],
    faqQuestions: [
      "How much will SCE rates increase in 2026?",
      "Is solar worth it with SCE?",
      "How to lower my SCE electricity bill?"
    ],
    internalLinks: ["/savings-estimate", "/blog", "/compare/utility-vs-ppa"]
  },
  {
    id: "escape-sdge-rates",
    keyword: "SDG&E highest rates in nation",
    searchVolume: "medium",
    intent: "informational",
    difficulty: "low",
    category: "utility-escape",
    targetPage: "/blog/sdge-highest-rates-california-solar",
    metaTitle: "SDG&E Highest Rates in USA | Escape with Solar 2026",
    metaDescription: "SDG&E charges $ Zero.52/kWh—highest in the nation. See how solar + battery cuts your bill 70%+.",
    h1Suggestion: "SDG&E Has the Highest Electricity Rates in America",
    relatedKeywords: ["SDG&E rates 2026", "highest electricity rates USA", "San Diego electricity costs"],
    faqQuestions: [
      "Why are SDG&E rates so high?",
      "How much does SDG&E charge per kWh?",
      "Is San Diego the most expensive for electricity?"
    ],
    internalLinks: ["/savings-estimate", "/california-solar-statistics-2026", "/service-areas/san-diego"]
  },
  {
    id: "california-utility-rates-2026",
    keyword: "California electricity rates 2026",
    searchVolume: "high",
    intent: "informational",
    difficulty: "medium",
    category: "utility-escape",
    targetPage: "/california-solar-statistics-2026",
    metaTitle: "California Electricity Rates 2026 | PG&E, SCE, SDG&E",
    metaDescription: "Complete guide to CA utility rates in 2026. Compare PG&E, SCE, SDG&E rates and learn how to cut costs.",
    h1Suggestion: "California Electricity Rates in 2026: Complete Breakdown",
    relatedKeywords: ["CA electric rates", "California utility costs", "electricity prices California"],
    faqQuestions: [
      "What is the average electricity rate in California?",
      "Which California utility is cheapest?",
      "Why is electricity so expensive in California?"
    ],
    internalLinks: ["/savings-estimate", "/blog", "/glossary"]
  },
  {
    id: "lower-electric-bill-california",
    keyword: "how to lower electric bill California",
    searchVolume: "high",
    intent: "informational",
    difficulty: "medium",
    category: "utility-escape",
    targetPage: "/blog/lower-electric-bill-california-2026",
    metaTitle: "Lower Your Electric Bill in California 2026 | 7 Ways",
    metaDescription: "Cut your CA electric bill by 70%+. Top strategies including $Zero down solar + battery storage.",
    h1Suggestion: "7 Ways to Lower Your Electric Bill in California",
    relatedKeywords: ["reduce electricity bill CA", "save money on electricity", "cut electric costs"],
    faqQuestions: [
      "What's the best way to lower my electric bill?",
      "Does solar really save money in California?",
      "How much can I save with solar in California?"
    ],
    internalLinks: ["/savings-estimate", "/#calculator", "/blog"]
  },

  // ========== NEM 3.0 KEYWORDS ==========
  {
    id: "nem-3-explained",
    keyword: "NEM 3.0 California explained",
    searchVolume: "high",
    intent: "informational",
    difficulty: "medium",
    category: "nem-3",
    targetPage: "/learning-center",
    metaTitle: "NEM 3.0 California Explained | What Homeowners Need to Know",
    metaDescription: "Simple guide to NEM 3.0. How California's net billing affects solar savings and why battery storage matters.",
    h1Suggestion: "NEM 3.0 Explained: California's New Solar Rules",
    relatedKeywords: ["net energy metering 3.0", "California net billing", "NEM 3.0 vs NEM 2.0"],
    faqQuestions: [
      "What is NEM 3.0 in California?",
      "Is solar still worth it under NEM 3.0?",
      "What's the difference between NEM 2.0 and NEM 3.0?"
    ],
    internalLinks: ["/learning-center", "/blog/nem-3-battery-calculator-guide", "/savings-estimate"]
  },
  {
    id: "nem-3-battery-required",
    keyword: "NEM 3.0 battery storage required",
    searchVolume: "medium",
    intent: "informational",
    difficulty: "low",
    category: "nem-3",
    targetPage: "/blog/nem-3-battery-calculator-guide",
    metaTitle: "Is Battery Required for NEM 3.0? | California Solar 2026",
    metaDescription: "Battery storage is essential under NEM 3.0. Learn why and calculate your optimal battery size.",
    h1Suggestion: "Why Battery Storage is Essential Under NEM 3.0",
    relatedKeywords: ["NEM 3.0 battery calculator", "solar battery California", "NEM 3.0 self consumption"],
    faqQuestions: [
      "Do I need a battery for solar under NEM 3.0?",
      "How much battery storage do I need?",
      "What happens without battery under NEM 3.0?"
    ],
    internalLinks: ["/equipment/tesla-powerwall-3", "/equipment/enphase-iq-battery", "/savings-estimate"]
  },
  {
    id: "solar-worth-it-nem-3",
    keyword: "is solar worth it in California 2026",
    searchVolume: "high",
    intent: "commercial",
    difficulty: "high",
    category: "nem-3",
    targetPage: "/blog/solar-panels-california-2026-complete-guide",
    metaTitle: "Is Solar Worth It in California 2026? | Complete Analysis",
    metaDescription: "Yes, solar still saves 65-75% with battery storage. See real numbers and calculate your savings.",
    h1Suggestion: "Is Solar Worth It in California in 2026?",
    relatedKeywords: ["California solar 2026", "should I go solar California", "solar ROI California"],
    faqQuestions: [
      "Is solar still a good investment in California?",
      "How long until solar pays for itself?",
      "What are the downsides of solar in California?"
    ],
    internalLinks: ["/savings-estimate", "/california-solar-statistics-2026", "/reviews"]
  },

  // ========== COST & SAVINGS KEYWORDS ==========
  {
    id: "solar-cost-california",
    keyword: "solar panel cost California 2026",
    searchVolume: "high",
    intent: "commercial",
    difficulty: "high",
    category: "cost-savings",
    targetPage: "/blog/solar-cost-per-watt-california-2026",
    metaTitle: "Solar Panel Cost California 2026 | $ Zero Down Options",
    metaDescription: "CA solar costs $2.50-$3.50/watt. But most choose $Zero down PPA. Get your free savings estimate.",
    h1Suggestion: "How Much Do Solar Panels Cost in California in 2026?",
    relatedKeywords: ["solar price California", "cost per watt California", "solar installation cost CA"],
    faqQuestions: [
      "How much does a solar system cost in California?",
      "What's the cost per watt for solar in California?",
      "Can I get solar with no money down?"
    ],
    internalLinks: ["/savings-estimate", "/#calculator", "/compare/utility-vs-ppa"]
  },
  {
    id: "zero-down-solar",
    keyword: "$Zero down solar California",
    searchVolume: "high",
    intent: "transactional",
    difficulty: "medium",
    category: "cost-savings",
    targetPage: "/ppa",
    metaTitle: "$ Zero Down Solar California | PPA Solar Agreement 2026",
    metaDescription: "Go solar with no upfront cost. Battery storage included. Start saving on day one with $Zero down PPA.",
    h1Suggestion: "$ Zero Down Solar in California: How PPA Works",
    relatedKeywords: ["no money down solar", "free solar panels California", "solar PPA California"],
    faqQuestions: [
      "How does $Zero down solar work?",
      "Is $Zero down solar really free?",
      "What's the catch with no money down solar?"
    ],
    internalLinks: ["/ppa", "/savings-estimate", "/compare/utility-vs-ppa"]
  },
  {
    id: "solar-savings-california",
    keyword: "how much do solar panels save California",
    searchVolume: "high",
    intent: "informational",
    difficulty: "medium",
    category: "cost-savings",
    targetPage: "/blog/how-much-solar-panels-save-california",
    metaTitle: "Solar Panel Savings California 2026 | Real Numbers",
    metaDescription: "CA homeowners save 65-75% on electricity with solar + battery. See real examples by utility area.",
    h1Suggestion: "How Much Do Solar Panels Save in California?",
    relatedKeywords: ["solar panel savings", "solar energy savings CA", "California solar bill reduction"],
    faqQuestions: [
      "How much can I save with solar in California?",
      "What percentage do solar panels save?",
      "How long does it take for solar to pay off?"
    ],
    internalLinks: ["/savings-estimate", "/#calculator", "/case-studies"]
  },
  {
    id: "ppa-solar-agreement",
    keyword: "what is a solar PPA",
    searchVolume: "medium",
    intent: "informational",
    difficulty: "low",
    category: "cost-savings",
    targetPage: "/ppa",
    metaTitle: "What is a Solar PPA? | Power Purchase Agreement Explained",
    metaDescription: "Solar PPA: pay only for power produced, $ Zero upfront. Learn how PPAs work and if it's right for you.",
    h1Suggestion: "What is a Solar PPA (Power Purchase Agreement)?",
    relatedKeywords: ["power purchase agreement solar", "PPA vs lease", "PPA vs buying solar"],
    faqQuestions: [
      "What is a PPA for solar panels?",
      "Is a solar PPA a good deal?",
      "What happens to PPA when I sell my home?"
    ],
    internalLinks: ["/ppa", "/compare/utility-vs-ppa", "/savings-estimate"]
  },

  // ========== BATTERY STORAGE KEYWORDS ==========
  {
    id: "best-solar-battery-california",
    keyword: "best solar battery California 2026",
    searchVolume: "medium",
    intent: "commercial",
    difficulty: "medium",
    category: "battery-storage",
    targetPage: "/compare/tesla-powerwall-vs-enphase-battery",
    metaTitle: "Best Solar Battery California 2026 | Tesla vs Enphase",
    metaDescription: "Compare Tesla Powerwall 3, Enphase IQ Battery, and Franklin. Find the best battery for your CA home.",
    h1Suggestion: "Best Solar Batteries for California Homes in 2026",
    relatedKeywords: ["home battery California", "solar battery comparison", "battery backup California"],
    faqQuestions: [
      "What's the best home battery in California?",
      "Tesla Powerwall vs Enphase: which is better?",
      "How much does a solar battery cost?"
    ],
    internalLinks: ["/equipment/tesla-powerwall-3", "/equipment/enphase-iq-battery", "/equipment/franklin-whole-home-battery"]
  },
  {
    id: "tesla-powerwall-california",
    keyword: "Tesla Powerwall 3 California",
    searchVolume: "medium",
    intent: "commercial",
    difficulty: "medium",
    category: "battery-storage",
    targetPage: "/equipment/tesla-powerwall-3",
    metaTitle: "Tesla Powerwall 3 California | Specs, Cost & Installation",
    metaDescription: "Tesla Powerwall 3: 13.5 kWh storage, whole home backup. Available with $Zero down PPA in California.",
    h1Suggestion: "Tesla Powerwall 3 in California: Complete Guide",
    relatedKeywords: ["Powerwall 3 specs", "Tesla battery California", "Powerwall cost California"],
    faqQuestions: [
      "How much does Tesla Powerwall 3 cost in California?",
      "Is Powerwall 3 worth it?",
      "Can I get Powerwall with no money down?"
    ],
    internalLinks: ["/equipment/tesla-powerwall-3", "/compare/tesla-powerwall-vs-enphase-battery", "/savings-estimate"]
  },
  {
    id: "home-battery-backup",
    keyword: "home battery backup California",
    searchVolume: "medium",
    intent: "commercial",
    difficulty: "low",
    category: "battery-storage",
    targetPage: "/learning-center",
    metaTitle: "Home Battery Backup California | Power Outage Protection",
    metaDescription: "Keep your lights on during PSPS shutoffs. Solar + battery backup for California homes. $Zero down.",
    h1Suggestion: "Home Battery Backup for California Power Outages",
    relatedKeywords: ["backup power California", "PSPS battery backup", "power outage protection"],
    faqQuestions: [
      "How long can a battery power my home?",
      "What size battery do I need for backup?",
      "Do solar batteries work during power outages?"
    ],
    internalLinks: ["/equipment/tesla-powerwall-3", "/equipment/franklin-whole-home-battery", "/savings-estimate"]
  },
  {
    id: "blackout-protection",
    keyword: "blackout protection California",
    searchVolume: "medium",
    intent: "transactional",
    difficulty: "low",
    category: "battery-storage",
    targetPage: "/learning-center",
    metaTitle: "Blackout Protection California | Solar Battery Backup",
    metaDescription: "Protect your home from CA blackouts. Solar + battery keeps power on during outages. $Zero down options.",
    h1Suggestion: "Blackout Protection for California Homes",
    relatedKeywords: ["power outage protection", "PSPS protection", "wildfire blackout battery"],
    faqQuestions: [
      "How do I protect my home from blackouts?",
      "Will solar work during a power outage?",
      "What's the best blackout protection for California?"
    ],
    internalLinks: ["/equipment/tesla-powerwall-3", "/savings-estimate", "/case-studies"]
  },

  // ========== LOCAL SEO KEYWORDS ==========
  {
    id: "solar-los-angeles",
    keyword: "best solar company Los Angeles 2026",
    searchVolume: "high",
    intent: "transactional",
    difficulty: "high",
    category: "local-seo",
    targetPage: "/service-areas/los-angeles",
    metaTitle: "Best Solar Company Los Angeles 2026 | $ Zero Down",
    metaDescription: "Top-rated solar organization in LA. $Zero down PPA with battery storage. Serving all LA County.",
    h1Suggestion: "Best Solar Company in Los Angeles (2026)",
    relatedKeywords: ["LA solar installers", "solar panels Los Angeles", "Los Angeles solar company"],
    faqQuestions: [
      "Who is the best solar company in LA?",
      "How much does solar cost in Los Angeles?",
      "Is solar worth it in Los Angeles?"
    ],
    internalLinks: ["/service-areas/los-angeles", "/savings-estimate", "/reviews"]
  },
  {
    id: "solar-san-diego",
    keyword: "best solar company San Diego 2026",
    searchVolume: "high",
    intent: "transactional",
    difficulty: "high",
    category: "local-seo",
    targetPage: "/service-areas/san-diego",
    metaTitle: "Best Solar Company San Diego 2026 | Escape SDG&E Rates",
    metaDescription: "SDG&E customers save 70%+ with solar + battery. Top San Diego installer. $Zero down PPA available.",
    h1Suggestion: "Best Solar Company in San Diego (2026)",
    relatedKeywords: ["San Diego solar installers", "solar panels San Diego", "SDG&E solar alternative"],
    faqQuestions: [
      "Who is the best solar company in San Diego?",
      "How much can I save with solar in San Diego?",
      "Is solar worth it with SDG&E rates?"
    ],
    internalLinks: ["/service-areas/san-diego", "/savings-estimate", "/reviews"]
  },
  {
    id: "solar-bay-area",
    keyword: "best solar company Bay Area 2026",
    searchVolume: "high",
    intent: "transactional",
    difficulty: "high",
    category: "local-seo",
    targetPage: "/service-areas",
    metaTitle: "Best Solar Company Bay Area 2026 | PG&E Alternative",
    metaDescription: "Escape PG&E rate hikes. Top Bay Area solar + battery installer. $Zero down PPA with 25-year warranty.",
    h1Suggestion: "Best Solar Company in the Bay Area (2026)",
    relatedKeywords: ["Bay Area solar installers", "solar San Francisco", "solar San Jose"],
    faqQuestions: [
      "Who is the best solar company in the Bay Area?",
      "How much does solar cost in the Bay Area?",
      "Is solar worth it in San Francisco?"
    ],
    internalLinks: ["/service-areas", "/savings-estimate", "/reviews"]
  },
  {
    id: "solar-installer-near-me",
    keyword: "solar installer near me California",
    searchVolume: "high",
    intent: "transactional",
    difficulty: "high",
    category: "local-seo",
    targetPage: "/service-areas",
    metaTitle: "Solar Installer Near Me | California Solar Company",
    metaDescription: "Find a trusted solar installer in your California city. $Zero down options. Free savings estimate.",
    h1Suggestion: "Find a Solar Installer Near You in California",
    relatedKeywords: ["local solar company", "solar contractors near me", "solar installation near me"],
    faqQuestions: [
      "How do I find a good solar installer near me?",
      "What should I look for in a solar company?",
      "Is my local solar company trustworthy?"
    ],
    internalLinks: ["/service-areas", "/savings-estimate", "/reviews"]
  },

  // ========== COMPARISON KEYWORDS ==========
  {
    id: "sunrun-vs-local",
    keyword: "Sunrun vs local solar company",
    searchVolume: "medium",
    intent: "commercial",
    difficulty: "medium",
    category: "comparison",
    targetPage: "/compare/sunrun",
    metaTitle: "Sunrun vs Local Solar Company | Honest Comparison 2026",
    metaDescription: "Compare Sunrun to local California installers. See pricing, warranties, and customer reviews side by side.",
    h1Suggestion: "Sunrun vs Local Solar Companies: Which is Better?",
    relatedKeywords: ["Sunrun reviews", "Sunrun alternatives", "Sunrun California"],
    faqQuestions: [
      "Is Sunrun worth it?",
      "What are the disadvantages of Sunrun?",
      "Is a local solar company better than Sunrun?"
    ],
    internalLinks: ["/compare/sunrun", "/reviews", "/savings-estimate"]
  },
  {
    id: "tesla-solar-alternative",
    keyword: "Tesla solar alternative California",
    searchVolume: "medium",
    intent: "commercial",
    difficulty: "low",
    category: "comparison",
    targetPage: "/compare/tesla-energy",
    metaTitle: "Tesla Solar Alternatives California 2026 | Better Options",
    metaDescription: "Compare Tesla Energy to local installers. Get Tesla equipment with local service and support.",
    h1Suggestion: "Tesla Solar Alternatives in California",
    relatedKeywords: ["Tesla solar reviews", "Tesla solar problems", "alternative to Tesla solar"],
    faqQuestions: [
      "Is Tesla solar any good?",
      "What are the problems with Tesla solar?",
      "Can I get Powerwall without Tesla solar?"
    ],
    internalLinks: ["/compare/tesla-energy", "/equipment/tesla-powerwall-3", "/savings-estimate"]
  },
  {
    id: "utility-vs-solar",
    keyword: "utility rates vs solar California",
    searchVolume: "medium",
    intent: "informational",
    difficulty: "low",
    category: "comparison",
    targetPage: "/compare/utility-vs-ppa",
    metaTitle: "Utility Rates vs Solar PPA California | True Cost Comparison",
    metaDescription: "Compare staying with your utility vs going solar. See 25-year projections for PG&E, SCE, SDG&E.",
    h1Suggestion: "Utility Bills vs Solar PPA: The True Cost Comparison",
    relatedKeywords: ["utility vs solar savings", "solar vs electric bill", "PPA vs utility rates"],
    faqQuestions: [
      "Is solar cheaper than utility power?",
      "How much do utility rates increase each year?",
      "Will I save money going solar?"
    ],
    internalLinks: ["/compare/utility-vs-ppa", "/savings-estimate", "/california-solar-statistics-2026"]
  },

  // ========== GENERAL HIGH-INTENT KEYWORDS ==========
  {
    id: "best-solar-company-california",
    keyword: "best solar company California 2026",
    searchVolume: "high",
    intent: "transactional",
    difficulty: "high",
    category: "general",
    targetPage: "/blog/best-solar-company-california-2026",
    metaTitle: "Best Solar Company California 2026 | Top 10 Installers",
    metaDescription: "Find the best solar company in California. Compare installers, warranties, and pricing. $Zero down options.",
    h1Suggestion: "Best Solar Companies in California for 2026",
    relatedKeywords: ["top solar companies California", "California solar installers", "best solar installer CA"],
    faqQuestions: [
      "Who is the best solar company in California?",
      "How do I choose a solar company?",
      "What makes a good solar installer?"
    ],
    internalLinks: ["/savings-estimate", "/reviews", "/compare"]
  },
  {
    id: "go-solar-california",
    keyword: "how to go solar California",
    searchVolume: "medium",
    intent: "informational",
    difficulty: "medium",
    category: "general",
    targetPage: "/learning-center",
    metaTitle: "How to Go Solar in California 2026 | Step by Step Guide",
    metaDescription: "Complete guide to going solar in CA. From consultation to installation. $Zero down options available.",
    h1Suggestion: "How to Go Solar in California: Step-by-Step Guide",
    relatedKeywords: ["going solar California", "solar installation process", "steps to go solar"],
    faqQuestions: [
      "What are the steps to go solar?",
      "How long does it take to go solar?",
      "What do I need to go solar?"
    ],
    internalLinks: ["/learning-center", "/savings-estimate", "/#how-it-works"]
  },
  {
    id: "california-solar-incentives",
    keyword: "California solar incentives 2026",
    searchVolume: "high",
    intent: "informational",
    difficulty: "medium",
    category: "general",
    targetPage: "/learning-center",
    metaTitle: "California Solar Incentives 2026 | Tax Credits & Rebates",
    metaDescription: "Complete list of CA solar incentives for 2026. Federal tax credit, state rebates, and utility programs.",
    h1Suggestion: "California Solar Incentives and Tax Credits for 2026",
    relatedKeywords: ["solar tax credit California", "California solar rebates", "federal solar credit 2026"],
    faqQuestions: [
      "What solar incentives are available in California?",
      "How much is the federal solar tax credit?",
      "Are there state rebates for solar in California?"
    ],
    internalLinks: ["/learning-center", "/savings-estimate", "/ppa"]
  },
  {
    id: "residential-solar-california",
    keyword: "residential solar California",
    searchVolume: "high",
    intent: "commercial",
    difficulty: "high",
    category: "general",
    targetPage: "/",
    metaTitle: "Residential Solar California | $ Zero Down Solar + Battery 2026",
    metaDescription: "California residential solar experts. $Zero down PPA with battery storage. Escape utility rate hikes.",
    h1Suggestion: "Residential Solar for California Homeowners",
    relatedKeywords: ["home solar California", "solar panels for home CA", "residential solar installation"],
    faqQuestions: [
      "Is residential solar worth it in California?",
      "How much does residential solar cost?",
      "Can I get solar with no upfront cost?"
    ],
    internalLinks: ["/", "/savings-estimate", "/#calculator"]
  },
  {
    id: "solar-panels-california",
    keyword: "solar panels California",
    searchVolume: "high",
    intent: "commercial",
    difficulty: "high",
    category: "general",
    targetPage: "/blog/solar-panels-california-2026-complete-guide",
    metaTitle: "Solar Panels California 2026 | Complete Homeowner's Guide",
    metaDescription: "Everything about solar panels in California. NEM 3.0, battery storage, $Zero down options. Start saving.",
    h1Suggestion: "Solar Panels in California: The Complete Guide",
    relatedKeywords: ["CA solar panels", "California solar installation", "solar panel system CA"],
    faqQuestions: [
      "Are solar panels worth it in California?",
      "How many solar panels do I need?",
      "What type of solar panels are best?"
    ],
    internalLinks: ["/blog/solar-panels-california-2026-complete-guide", "/savings-estimate", "/equipment"]
  },
  {
    id: "energy-independence-california",
    keyword: "energy independence California",
    searchVolume: "low",
    intent: "informational",
    difficulty: "low",
    category: "general",
    targetPage: "/",
    metaTitle: "Energy Independence California | Solar + Battery Freedom",
    metaDescription: "Achieve energy independence from CA utilities. Solar + battery storage. Protection from rate hikes.",
    h1Suggestion: "Achieve Energy Independence in California",
    relatedKeywords: ["off grid California", "energy self sufficiency", "utility independence"],
    faqQuestions: [
      "Can I be energy independent in California?",
      "How do I achieve energy independence?",
      "What do I need for energy independence?"
    ],
    internalLinks: ["/", "/savings-estimate", "/learning-center"]
  },
];

// ============================================
// KEYWORD CATEGORIES FOR PAGE-LEVEL SEO
// ============================================

export const keywordCategories = {
  homepage: [
    "residential solar California",
    "$Zero down solar California",
    "energy independence California",
    "California solar 2026"
  ],
  savingsEstimate: [
    "solar savings calculator California",
    "how much do solar panels save",
    "escape utility rate hikes",
    "$Zero down solar"
  ],
  learningCenter: [
    "NEM 3.0 California explained",
    "how to go solar California",
    "California solar incentives 2026",
    "solar battery California"
  ],
  ppa: [
    "what is a solar PPA",
    "$Zero down solar California",
    "solar PPA vs lease",
    "power purchase agreement solar"
  ],
  equipmentPages: [
    "Tesla Powerwall 3 California",
    "Enphase IQ Battery",
    "Franklin home battery",
    "best solar battery California"
  ],
  comparisons: [
    "Tesla Powerwall vs Enphase",
    "Sunrun vs local solar",
    "utility rates vs solar",
    "best solar company California"
  ],
  serviceAreas: [
    "solar installer near me",
    "best solar company Los Angeles",
    "best solar company San Diego",
    "Bay Area solar installer"
  ],
  utilityEscape: [
    "escape PG&E rate hikes",
    "escape SCE rate hikes",
    "SDG&E highest rates",
    "lower electric bill California"
  ]
};

// ============================================
// SEO META TEMPLATES
// ============================================

export const seoMetaTemplates = {
  cityPage: (city: string, utility: string) => ({
    title: `Solar Panels ${city} CA | ${utility} Bill Savings 2026`,
    description: `${city} homeowners save 65-75% on ${utility} bills with solar + battery. $Zero down PPA. Free savings estimate.`,
    keywords: `solar ${city.toLowerCase()}, ${city.toLowerCase()} solar installer, ${utility.toLowerCase()} alternative, solar panels ${city.toLowerCase()} california`
  }),
  countyPage: (county: string) => ({
    title: `Solar Panels ${county} County CA | Top Installer 2026`,
    description: `Serving all ${county} County cities. Solar + battery with $Zero down PPA. Escape utility rate hikes.`,
    keywords: `solar ${county.toLowerCase()} county, ${county.toLowerCase()} county solar installer, solar panels ${county.toLowerCase()}`
  }),
  blogPost: (topic: string, utility?: string) => ({
    title: utility 
      ? `${topic} | ${utility} Solar Guide 2026`
      : `${topic} | California Solar Guide 2026`,
    description: `Expert guide: ${topic.toLowerCase()}. Updated for 2026. California Energy Group.`,
    keywords: `${topic.toLowerCase()}, california solar, ${utility?.toLowerCase() || 'solar energy'}, 2026`
  })
};

// ============================================
// LONG-TAIL KEYWORD PHRASES
// ============================================

export const longTailKeywords = [
  // Utility-specific escape queries
  "how to get rid of PG&E",
  "alternatives to SDG&E high rates",
  "escape Southern California Edison rates",
  "stop paying high electric bills California",
  "PG&E rate hike protection",
  "SCE rate increase 2026 solar",
  "SDG&E customers switching to solar",
  
  // Cost and savings queries
  "how much can I save with solar in California",
  "is solar worth it with NEM 3.0",
  "solar panel payback period California",
  "no money down solar California",
  "free solar panels California 2026",
  "solar lease vs PPA California",
  
  // Battery-specific queries
  "how much battery storage do I need",
  "best battery for NEM 3.0",
  "solar battery backup for power outages",
  "Tesla Powerwall vs Enphase for California",
  "whole home battery backup California",
  
  // Local search queries
  "solar companies in my area California",
  "local solar installer reviews California",
  "solar panel installation near me CA",
  
  // Decision-stage queries
  "best time to go solar California",
  "questions to ask solar installer",
  "solar company red flags to avoid",
  "how to choose solar installer California"
];

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getKeywordsByCategory = (category: SEOKeyword["category"]): SEOKeyword[] => {
  return seoKeywords.filter(k => k.category === category);
};

export const getHighVolumeKeywords = (): SEOKeyword[] => {
  return seoKeywords.filter(k => k.searchVolume === "high");
};

export const getTransactionalKeywords = (): SEOKeyword[] => {
  return seoKeywords.filter(k => k.intent === "transactional");
};

export const getKeywordById = (id: string): SEOKeyword | undefined => {
  return seoKeywords.find(k => k.id === id);
};

export const getAllKeywordStrings = (): string[] => {
  const allKeywords: string[] = [];
  seoKeywords.forEach(k => {
    allKeywords.push(k.keyword);
    allKeywords.push(...k.relatedKeywords);
  });
  return [...new Set(allKeywords)];
};

export const getKeywordsForPage = (pagePath: string): SEOKeyword[] => {
  return seoKeywords.filter(k => k.targetPage === pagePath || k.internalLinks.includes(pagePath));
};
